import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Gem, Loader, Users } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'

const formatFinishedAgo = (secondsValue, t) => {
  const totalSeconds = Math.max(0, Number(secondsValue || 0))

  if (totalSeconds < 60) {
    return t('double.history.timeAgoSec', '{{count}} сек назад', { count: totalSeconds })
  }

  const minutes = Math.floor(totalSeconds / 60)
  if (minutes < 60) {
    return t('double.history.timeAgoMin', '{{count}} мин назад', { count: minutes })
  }

  const hours = Math.floor(minutes / 60)
  return t('double.history.timeAgoHour', '{{count}} ч назад', { count: hours })
}

const DoubleHistory = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { loadHistory } = useDoubleStore()

  const [activeTab, setActiveTab] = useState('all')
  const [allRounds, setAllRounds] = useState([])
  const [myRounds, setMyRounds] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const rounds = activeTab === 'all' ? allRounds : myRounds

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true)
      try {
        const data = await loadHistory()
        setAllRounds(data.all || [])
        setMyRounds(data.mine || [])
      } catch (error) {
        toast.error(t('double.history.loadError', 'Не удалось загрузить историю'))
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [loadHistory, t])

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8">
      <div className="mx-auto max-w-xl space-y-6">
        <div className="relative flex items-center justify-center lg:min-h-[48px]">
          <button
            onClick={() => navigate('/double')}
            className="absolute left-0 inline-flex items-center justify-center rounded-xl border border-white/10 bg-surface p-2.5 text-white hover:text-accent-primary"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl lg:text-4xl font-bold text-white">{t('double.history.title', 'История Double')}</h1>
        </div>

        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setActiveTab('all')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'all' ? 'bg-accent-primary text-dark-primary' : 'bg-surface text-text-secondary'
            }`}
          >
            {t('double.history.tabs.all', 'Все')}
          </button>
          <button
            onClick={() => setActiveTab('my')}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === 'my' ? 'bg-accent-primary text-dark-primary' : 'bg-surface text-text-secondary'
            }`}
          >
            {t('double.history.tabs.my', 'Мои')}
          </button>
        </div>

        {isLoading ? (
          <div className="flex min-h-[300px] w-full items-center justify-center rounded-2xl border border-white/10 bg-surface/50">
            <Loader className="w-8 h-8 text-accent-primary animate-spin" />
          </div>
        ) : rounds.length === 0 ? (
          <div className="w-full rounded-2xl border border-white/10 bg-surface/50 p-8 text-center text-text-secondary">
            {t('double.history.empty', 'История пуста')}
          </div>
        ) : (
          <div className="grid w-full grid-cols-1 gap-3">
            {rounds.map((round) => {
              const totalRoundSum = Number(round.sum_red || 0) + Number(round.sum_black || 0) + Number(round.sum_green || 0)
              const playersCount = Array.isArray(round.users) ? round.users.length : 0

              const colorTheme = round.win_color === 'red'
                ? {
                    cardClass: 'border-red-300/30 bg-red-500/5 hover:border-red-200/55',
                    badgeClass: 'bg-red-500/15 text-red-200 border border-red-300/35',
                    dotClass: 'bg-red-400'
                  }
                : round.win_color === 'black'
                  ? {
                      cardClass: 'border-slate-300/25 bg-slate-700/10 hover:border-slate-200/45',
                      badgeClass: 'bg-slate-600/25 text-slate-100 border border-slate-300/35',
                      dotClass: 'bg-slate-200'
                    }
                  : {
                      cardClass: 'border-emerald-300/30 bg-emerald-500/5 hover:border-emerald-200/55',
                      badgeClass: 'bg-emerald-500/15 text-emerald-200 border border-emerald-300/35',
                      dotClass: 'bg-emerald-400'
                    }

              return (
              <button
                key={round.id}
                onClick={() => navigate(`/double/history/${round.id}`)}
                className={`rounded-2xl border px-4 py-3 text-left transition-colors ${colorTheme.cardClass}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-text-secondary">{formatFinishedAgo(round.finished_sec_ago, t)}</p>
                  <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${colorTheme.badgeClass}`}>
                    <span className={`h-2 w-2 rounded-full ${colorTheme.dotClass}`} />
                    <span>{round.win_sector}</span>
                  </span>
                </div>

                <div className="mt-2 flex items-center justify-between gap-3 text-sm">
                  <div className="inline-flex items-center gap-3">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-white">
                      <Gem className="h-4 w-4 text-accent-primary" />
                      <span>{totalRoundSum.toLocaleString()}</span>
                    </span>

                    <span
                      className="inline-flex items-center gap-1.5 text-text-secondary"
                      aria-label={t('double.history.playersCount', 'Игроков: {{count}}', { count: playersCount })}
                    >
                      <Users className="h-4 w-4" />
                      <span>{playersCount}</span>
                    </span>
                  </div>

                  {round.you && (
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      round.you_win
                        ? 'border border-emerald-300/35 bg-emerald-500/15 text-emerald-100'
                        : 'border border-rose-300/35 bg-rose-500/15 text-rose-100'
                    }`}>
                      {round.you_win
                        ? t('double.history.youWon', 'Выиграл')
                        : t('double.history.youLost', 'Проиграл')}
                    </span>
                  )}
                </div>
              </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default DoubleHistory
